import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { money } from "../lib/products";
import { post, errMsg } from "../lib/api";
import { Field } from "../components/site/Field";
import { Eyebrow } from "../components/site/motion";

const INITIAL = { name: "", email: "", address: "", city: "", postal_code: "", country: "" };

const Summary = ({ items, subtotal }) => (
  <div className="rounded-[28px] border-2 border-dano-ink p-6 sm:p-8" data-testid="checkout-summary">
    <h2 className="font-wide text-2xl uppercase">Order summary</h2>
    <ul className="mt-4 divide-y divide-dano-ink/15">
      {items.map((i) => (
        <li key={i.key} className="flex justify-between gap-4 py-3">
          <span>{i.name} · {i.variantLabel} × {i.qty}<br /><span className="text-sm text-dano-red">Coming soon</span></span>
          <span className="font-cond text-lg">{money(i.price * i.qty)}</span>
        </li>
      ))}
    </ul>
    <div className="mt-4 flex justify-between border-t-2 border-dano-ink pt-4 font-cond text-xl uppercase">
      <span>Subtotal</span><span data-testid="checkout-subtotal">{money(subtotal)}</span>
    </div>
  </div>
);

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const [values, setValues] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const bind = (name) => ({ value: values[name], onChange: (e) => setValues((v) => ({ ...v, [name]: e.target.value })) });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await post("/orders", { customer: values, items: items.map((i) => ({ slug: i.slug, variant: i.variant, qty: i.qty })) });
      setOrder(data);
      clear();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (order) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-36 pb-32" data-testid="order-confirmation">
        <Eyebrow>Test order placed</Eyebrow>
        <h1 className="mt-4 font-wide text-5xl sm:text-6xl uppercase leading-[0.9]">Thanks, {order?.customer?.name?.split(" ")[0] ?? "friend"}.</h1>
        <p className="mt-6 text-lg">Order <strong data-testid="order-number">{order?.order_number}</strong> was saved as a test order for {money(order?.subtotal)}. No payment was taken and nothing will ship yet.</p>
        <Link to="/" data-testid="order-home-link" className="btn-press mt-10 inline-flex rounded-full bg-dano-ink px-8 py-4 font-cond text-lg uppercase tracking-widest text-dano-cream hover:bg-dano-red">Back home</Link>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-40 pb-32 text-center" data-testid="checkout-empty">
        <h1 className="font-wide text-4xl uppercase">Your cart is empty</h1>
        <Link to="/shop" className="mt-6 inline-block font-cond uppercase tracking-widest text-dano-red underline">Go to shop</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pt-28 sm:pt-36 pb-24">
      <Eyebrow>Checkout</Eyebrow>
      <h1 className="mt-3 font-wide text-5xl sm:text-6xl uppercase leading-[0.9]">Almost there.</h1>
      <div className="mt-6 flex items-start gap-3 rounded-2xl bg-dano-gold p-4" data-testid="checkout-test-mode-notice">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
        <p className="text-sm sm:text-base"><strong>Test mode, placeholder payment.</strong> Products are coming soon. Your order is saved for testing only. No card is needed and no payment is taken.</p>
      </div>
      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <form onSubmit={submit} data-testid="checkout-form" className="grid gap-5 sm:grid-cols-2 lg:col-span-7">
          <Field label="Full name" required testId="checkout-name-input" {...bind("name")} />
          <Field label="Email" type="email" required testId="checkout-email-input" {...bind("email")} />
          <Field label="Address" required testId="checkout-address-input" className="sm:col-span-2" {...bind("address")} />
          <Field label="City" required testId="checkout-city-input" {...bind("city")} />
          <Field label="Postal code" required testId="checkout-postal-input" {...bind("postal_code")} />
          <Field label="Country" required testId="checkout-country-input" className="sm:col-span-2" {...bind("country")} />
          <div className="sm:col-span-2 rounded-2xl border-2 border-dashed border-dano-ink/40 p-5 text-dano-ink/80" data-testid="payment-placeholder">
            <p className="font-cond uppercase tracking-[0.18em]">Payment</p>
            <p className="mt-1 text-sm">Placeholder. Real payment will be connected before launch.</p>
          </div>
          <button type="submit" disabled={loading} data-testid="checkout-form-submit" className="btn-press sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-dano-red py-4 font-cond text-lg uppercase tracking-widest text-dano-cream hover:bg-dano-ink disabled:opacity-70">
            {loading && <Loader2 className="h-5 w-5 animate-spin" />} Place test order · {money(subtotal)}
          </button>
        </form>
        <div className="lg:col-span-4 lg:col-start-9"><Summary items={items} subtotal={subtotal} /></div>
      </div>
    </div>
  );
}
